import React from 'react';
import { useTrans } from 'base/utils/i18n';
import cn from 'classnames'
import { IconStar } from '@base/icon';
import type { IconStarType } from '@base/icon/IconStar';
import type { IconProps } from '@base/icon/interface';
import { LoopRender } from '@base/snippets/LoopRender';
import { get375CssValue } from 'base/utils/dom-size';

export type RateType = 'simple' | 'detailed';

export type IRateProps = {
    className?: string;
    style?: React.CSSProperties;
    type?: RateType;
    starSize?: IconProps['size'];
    textSize?: number;
    value?: number | string;
    total?: number | string;
    onClick?: () => void;
};

function getStarTypes(value: number | string): IconStarType[] {
    const splited = (Math.round(Number(value) * 2) / 2).toFixed(1).split('.');
    const fullLen = Number(splited[0]);
    const hasHalf = splited[1] !== '0';
    return new Array(5).fill(void 0).map((_, idx) => {
        if (idx < fullLen) return 'default';
        if (idx === fullLen && hasHalf) return 'half';
        return 'ghost';
    });
}

export const Rate: React.FC<IRateProps> = props => {
    const {
        className,
        style,
        value = 5,
        total,
        onClick,
        type = 'detailed',
        starSize,
        textSize,
        children,
    } = props;

    const { t } = useTrans();

    const textStyles = textSize ? { fontSize: get375CssValue(textSize) } : void 0;

    const getText = (children: React.ReactNode, type: RateType, total: number | string | undefined): React.ReactNode => {
        if (children) {
            return typeof children === 'function' ? children() : children;
        } else if (typeof total !== 'undefined') {
            let text = '';
            if (type === 'detailed') {
                text = Number(total) > 1 ? t('web_product_total_reviews', { total }) : t('web_product_total_review', { total });
            } else {
                text = `${total}`;
            }
            return <span className="sw-rate-text" style={textStyles}>({text})</span>;
        }
        return null;
    }

    return (
        <div
            className={cn(
                className,
                'sw-rate flex flex-middle',
                { pointer: !!onClick }
            )}
            style={style}
            onClick={onClick}
        >
            <LoopRender
                tag={false}
                list={getStarTypes(value)}
                render={type => (
                    <IconStar
                        type={type}
                        className="sw-rate-star"
                        size={starSize}
                    />
                )}
            />

            {getText(children, type, total)}
        </div>
    )
};
